/* eslint-disable @typescript-eslint/no-unused-vars */
import { AccordionProps } from "@components/accordion_group"
import {
  appContextDefaultValues,
  AppContextProps,
  useAppContext
} from "@contexts/app_context"
import { getCustomerByIdQuery } from "@domain/customer/infos"
import {
  CustomerPackageName,
  CustomerPeriodMode,
  getCustomers
} from "@domain/customer/list"
import { yupResolver } from "@hookform/resolvers/yup"
import useControlledForm from "@hooks/use_controlled_form"
import useToggle from "@hooks/use_toggle"
// import { CompanyFormData } from "@pages/customer/infos/tabs/company"
import { useQuery } from "@tanstack/react-query"
import i18next from "@utils/localization"
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState
} from "react"
import type {
  Control,
  UseFormHandleSubmit,
  UseFormReset
} from "react-hook-form"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import * as yup from "yup"
import {
  Customer,
  CustomerPerson,
  GetCustomerOwnersData
} from "__generated__/graphql"

export type CompanyFormData = {
  name: string
  website: string
  role: string
  channel: string
  industry: string
  size: string
  websiteBuild: string
  aboutUs: string
}

export type CustomerDetailFormData = {
  name?: string
  accountConfirmation?: string
  accountStatus?: string
  appStatus?: string
}

export type CustomerPageContextProps = AppContextProps & {
  isLeftDrawerOpen?: boolean
  toggleIsLeftDrawerOpen?: () => void
  navigationValue?: string
  handleClickNavigationRow?: (value: string) => void
  navigationColumns: AccordionProps[]
  setTabsValue?: Dispatch<SetStateAction<number>>
  tabsValue: number
  customerData?: {
    docs: GetCustomerOwnersData[] | null
    totalDocs: number | undefined
  }
  customers?: Customer[] | null
  currentCustomer?: Customer
  isCurrentCustomerLoading: boolean
  selectedCustomerId?: string
  setSelectedCustomerId?: Dispatch<SetStateAction<string | undefined>>
  setSearchData?: Dispatch<SetStateAction<string | undefined>>
  isValidCompany?: boolean
  isDirtyCompany?: boolean
  isValidCustomerDetailForm?: boolean
  isDirtyCustomerDetailForm?: boolean
  loading: boolean
  setLoading: (value: boolean) => void
  fetchProducts?: () => void
  resetCompany?: UseFormReset<CompanyFormData>
  resetCustomerDetailForm?: UseFormReset<CustomerDetailFormData>
  handleSubmitCompany?: UseFormHandleSubmit<CompanyFormData, CompanyFormData>
  handleSubmitCustomerDetailForm?: UseFormHandleSubmit<
    CustomerDetailFormData,
    CustomerDetailFormData
  >
  controlCompany: Control<CompanyFormData, unknown>
  controlCustomerDetailForm: Control<CustomerDetailFormData>
  users: CustomerPerson[]
  setUsers?: Dispatch<SetStateAction<CustomerPerson[]>>
  isLoading: boolean
  pagination?: {
    page: number
    pageSize: number
  }
  setPagination?: React.Dispatch<
    React.SetStateAction<{
      page: number
      pageSize: number
    }>
  >
  search?: string
  setSearch: (search: string) => void
  appStatus?: string
  setAppStatus: Dispatch<SetStateAction<string | undefined>>
  accountStatus?: string
  setAccountStatus: Dispatch<SetStateAction<string | undefined>>
  isEmailVerified?: boolean
  setIsEmailVerified: Dispatch<SetStateAction<boolean | undefined>>
  packageName?: CustomerPackageName
  setPackageName: Dispatch<SetStateAction<CustomerPackageName | undefined>>
  periodMode?: CustomerPeriodMode
  setPeriodMode: Dispatch<SetStateAction<CustomerPeriodMode | undefined>>
  isDeleteCustomerDialogOpen?: boolean
  toggleDeleteCustomerDialogOpen?: () => void
}

export const customerPageDefaultValues: CustomerPageContextProps = {
  ...appContextDefaultValues,
  translate: i18next.t,
  navigationColumns: [],
  users: [],
  tabsValue: 0,
  controlCompany: {} as Control<CompanyFormData>,
  controlCustomerDetailForm: {} as Control<CustomerDetailFormData>,
  isLoading: true,
  isCurrentCustomerLoading: false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setSearch: (search: string) => {},
  setAppStatus: () => {},
  setAccountStatus: () => {},
  setIsEmailVerified: () => {},
  setPackageName: () => {},
  setPeriodMode: () => {},
  loading: false,
  setLoading: () => {}
}

const CustomerPageContext = createContext<CustomerPageContextProps>(
  customerPageDefaultValues
)

export type paramsData = {
  pageNumber: number
  pageSize: number
  search?: string
}
export type CustomerOwner = {
  customerPerson: CustomerPerson
  customer: Customer
}
export type FormStateData = {
  isValid: boolean
  isDirty: boolean
}
export type UsersData = {
  id: string
  firstName: string
  email: string
  phone?: string
  lastName: string
  role: string
  status: string
  profilePhoto?: string
  isArchived?: boolean
}

export function CustomerPageContextProvider(props: {
  children: React.ReactNode | React.ReactNode[]
}) {
  const { translate, me } = useAppContext()

  const [isLeftDrawerOpen, toggleIsLeftDrawerOpen] = useToggle(true)
  const [isDeleteCustomerDialogOpen, toggleDeleteCustomerDialogOpen] =
    useToggle()

  const [navigationValue, setNavigationValue] = useState(
    window.location.pathname
  )

  const { id } = useParams()
  const [tabsValue, setTabsValue] = useState(0)
  const navigate = useNavigate()
  const location = useLocation()
  // const [customerData, setCustomerData] = useState<CustomerOwner[]>()
  // const [selectedCustomer, setSelectedCustomer] = useState<CustomerOwner>()
  const [searchData, setSearchData] = useState<string>()
  const [users, setUsers] = useState<CustomerPerson[]>([])
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 20
  })

  const [search, setSearch] = useState("")
  const [appStatus, setAppStatus] = useState<string>()
  const [accountStatus, setAccountStatus] = useState<string>()
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>()
  const [packageName, setPackageName] = useState<CustomerPackageName>()
  const [periodMode, setPeriodMode] = useState<CustomerPeriodMode>()
  const [customerData, setCustomerData] = useState<{
    docs: GetCustomerOwnersData[] | null
    totalDocs: number | undefined
  }>()
  const [customers, setCustomers] = useState<Customer[] | undefined>([])

  const [selectedCustomerId, setSelectedCustomerId] = useState<
    string | undefined
  >(undefined)

  // const customer = customers?.filter((i) => i._id === id)[0]
  const { isLoading } = useQuery({
    queryKey: [
      "customer-data-table",
      pagination,
      search,
      appStatus,
      accountStatus,
      isEmailVerified,
      packageName,
      periodMode
    ],
    queryFn: async () => {
      const response = await getCustomers({
        pageNumber: search ? 0 : pagination.page + 1,
        pageSize: pagination.pageSize,
        search,
        appStatus,
        accountStatus,
        isEmailVerified,
        packageName,
        periodMode
      })
      setCustomerData(response.data)
      setCustomers(
        response.data.docs?.map((item) => item?.customer ?? ({} as Customer))
      )
    },
    enabled: !id,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false
  })

  const { data: currentCustomer, isLoading: isCurrentCustomerLoading } =
    useQuery({
      queryKey: ["customer-by-id", id],
      queryFn: async () => {
        if (!id) {
          return undefined
        }
        const response = await getCustomerByIdQuery(id)
        if (!response.isSuccess) {
          return undefined
        }
        return response.data
      },
      enabled: !!id,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    })

  const companySchema = yup.object({
    name: yup.string().required(translate("name_field_is_required")),
    website: yup
      .string()
      .url(translate("invalid_url"))
      .required(translate("website_field_is_required")),
    role: yup.string().required(translate("role_field_is_required")),
    channel: yup.string().required(translate("channel_field_is_required")),
    industry: yup.string().required(translate("industry_field_is_required")),
    size: yup.string().required(translate("size_field_is_required")),
    websiteBuild: yup
      .string()
      .required(translate("website_build_field_is_required")),
    aboutUs: yup.string().required(translate("about_us_field_is_required"))
  })

  const {
    control: controlCompany,
    handleSubmit: handleSubmitCompany,
    reset: resetCompany,
    formState: { isValid: isValidCompany, isDirty: isDirtyCompany }
  } = useControlledForm<CompanyFormData>({
    resolver: yupResolver(companySchema),
    mode: "all"
  })

  const customerDetailSchema = yup.object({
    name: yup.string().optional(),
    accountConfirmation: yup.string().optional(),
    accountStatus: yup.string().optional(),
    appStatus: yup.string().optional()
  })

  const {
    control: controlCustomerDetailForm,
    handleSubmit: handleSubmitCustomerDetailForm,
    reset: resetCustomerDetailForm,
    formState: {
      isValid: isValidCustomerDetailForm,
      isDirty: isDirtyCustomerDetailForm
    }
  } = useControlledForm<CustomerDetailFormData>({
    resolver: yupResolver(customerDetailSchema),
    mode: "all"
  })

  function handleClickNavigationRow(value: string) {
    setNavigationValue(value)
    navigate(value)
  }

  console.log("current customer", currentCustomer)

  return (
    <CustomerPageContext.Provider
      value={{
        ...customerPageDefaultValues,
        translate,
        me,
        isLeftDrawerOpen,
        toggleIsLeftDrawerOpen,
        navigationValue,
        handleClickNavigationRow,
        customerData,
        customers,
        currentCustomer,
        isCurrentCustomerLoading,
        selectedCustomerId,
        setSelectedCustomerId,
        tabsValue,
        setTabsValue,
        setSearchData,
        isValidCompany,
        isDirtyCompany,
        isValidCustomerDetailForm,
        isDirtyCustomerDetailForm,
        resetCompany,
        resetCustomerDetailForm,
        handleSubmitCompany,
        handleSubmitCustomerDetailForm,
        controlCompany,
        controlCustomerDetailForm,
        users,
        setUsers,
        search,
        setSearch,
        appStatus,
        setAppStatus,
        accountStatus,
        setAccountStatus,
        isEmailVerified,
        setIsEmailVerified,
        packageName,
        setPackageName,
        periodMode,
        setPeriodMode,
        isLoading,
        pagination,
        setPagination,
        isDeleteCustomerDialogOpen,
        toggleDeleteCustomerDialogOpen
      }}
    >
      {props.children}
    </CustomerPageContext.Provider>
  )
}

export function useCustomerPageContext() {
  const context = useContext(CustomerPageContext)
  if (context === undefined) {
    throw new Error(
      "useCustomerPageContext must be used within an useCustomerPageContextProvider"
    )
  }
  return context
}
